import React, { useRef, useState } from 'react';
import { UploadCloud, FileVideo } from 'lucide-react';

interface DropzoneProps {
  onFileSelected: (file: File) => void;
  disabled?: boolean;
}

const Dropzone: React.FC<DropzoneProps> = ({ onFileSelected, disabled }) => {
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled) return;

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (file.type.startsWith('video/')) {
        onFileSelected(file);
      } else {
        alert("Please upload a video file.");
      }
    }
  };

  const handleClick = () => {
    if (!disabled && inputRef.current) {
      inputRef.current.click();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onFileSelected(e.target.files[0]);
    }
  };

  return (
    <div
      onClick={handleClick}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`
        w-full h-64 border-2 border-dashed rounded-2xl flex flex-col items-center justify-center cursor-pointer transition-all duration-300
        ${disabled ? 'opacity-50 cursor-not-allowed border-gray-700 bg-gray-900' : 
          isDragging ? 'border-blue-500 bg-blue-900/20' : 'border-gray-600 hover:border-blue-400 hover:bg-gray-800'
        }
      `}
    >
      <input
        type="file"
        ref={inputRef}
        onChange={handleInputChange}
        accept="video/*"
        className="hidden"
        disabled={disabled}
      />
      
      <div className="bg-gray-700 p-4 rounded-full mb-4">
        {isDragging ? <FileVideo className="w-8 h-8 text-blue-400" /> : <UploadCloud className="w-8 h-8 text-gray-300" />}
      </div>
      
      <h3 className="text-xl font-semibold mb-2 text-gray-200">
        {isDragging ? "Drop video here" : "Upload Video"}
      </h3>
      <p className="text-gray-400 text-sm text-center max-w-xs">
        Drag and drop your video file here, or click to browse.
        <br />
        <span className="text-xs text-gray-500">(MP4, WebM, MOV supported)</span>
      </p>
    </div>
  );
};

export default Dropzone;
